import type { Clip as ClipTyp, Op, Timeline } from '@schnitt/shared';
import { Clip, type SzenenDatei, anSzenen, fuellFaktor, keyframeAt, standbildPlan, teileClip, uebergangsZiel } from '@schnitt/shared';
import { newId } from './ids';
import type { MenuItem } from './components/ContextMenu';
import type { AssetView } from './api';
import { api } from './api';
import { duplicateClip, pausenRaus, splitAt, toggleKeyframe } from './edits';
import type { SheetName } from './components/Toolbar';
import { zeitText } from './zeit';
import { loescheClips } from './loeschen';

/**
 * Was man mit einem Clip tun kann -- an *einer* Stelle aufgeschrieben.
 *
 * Seit 7.70 trägt jeder Eintrag seine Gruppe: `schnell` (trennen,
 * duplizieren, löschen, Ton) steht oben als Zeichen, dann die Zeilen nach
 * Nähe -- Clip, Medium, Spur --, und `mehr` (Angaben, erneut aufbereiten,
 * Bildschirmformat) hinter einem Aufklapper. Was wohin gehört, prüft der
 * UI-Durchlauf.
 *
 * Die Liste stand in `Timeline.tsx` und war damit an die Zeitleiste gebunden.
 * Sascha, 8. September: „Elemente auf der Leinwand bekommen hoffentlich auch
 * noch ein Kontextmenü“ (7.61). Zwei Menüs nebeneinander wären zwei Listen,
 * die sich auseinanderentwickeln -- und dann kann man dasselbe an der einen
 * Stelle und an der anderen nicht.
 *
 * `at` ist die Stelle auf der Zeitleiste, an der gefragt wurde: in der
 * Zeitleiste, wo der Finger lag, auf der Leinwand der Abspielkopf. Daran
 * hängen „Hier trennen“ und der Keyframe.
 */
export interface ClipMenuUmgebung {
  /** Für das Standbild (S8): das Bild wird auf dem Server geholt. */
  projectId: string;
  /** Danach muss die Medienliste das neue Bild kennen -- sonst bleibt der Clip leer. */
  refreshAssets: () => Promise<void>;
  timeline: Timeline;
  settings: { width: number; height: number };
  fps: number;
  assetById: Map<string, AssetView>;
  selection: string[];
  dispatch: (op: Op | Op[]) => void;
  toast: (kind: 'success' | 'error' | 'info', text: string) => void;
  select: (ids: string[]) => void;
  setMultiSelect: (an: boolean) => void;
  setPlayhead: (t: number) => void;
  setErsetzen: (clipId: string | null) => void;
  setClipName: (wahl: { clipId: string; name: string } | null) => void;
  setAngaben: (clipId: string | null) => void;
  /** An welchem Clip der nächste Hinweis kleben soll. */
  setHinweisAnker: (clipId: string | null) => void;
  onOpenSheet: (sheet: SheetName) => void;
  /** Welche Bildspuren ihre Tonkurve aufgeklappt haben (7.65). */
  tonkurven: string[];
  tonkurveUmschalten: (trackId: string) => void;
}

export function clipMenuItems(u: ClipMenuUmgebung, clipId: string, at: number): MenuItem[] {
    const spur = u.timeline.tracks.find((t) => t.clips.some((c) => c.id === clipId));
    const found = spur?.clips.find((c) => c.id === clipId);
    if (!found || !spur) return [];
    const items: MenuItem[] = [
      {
        icon: 'scissors',
        label: 'Hier trennen',
        gruppe: 'schnell',
        onSelect: () => {
          const ops = splitAt(u.timeline, at, [clipId]);
          if (ops.length === 0) u.toast('info', 'Zu nah am Rand.');
          else u.dispatch(ops);
        },
      },
      { icon: 'sliders', label: 'Bearbeiten', gruppe: 'clip', onSelect: () => u.onOpenSheet('clip') },
      // Hinweise am Objekt. Sascha, in der App: „Mir bringt das Hinweismenü
      // nix. Es muss am Objekt hingeklebt sein. Auch am Objekt entstehen.“
      // Genau das ist dieser Eintrag: der Hinweis entsteht *hier*, am Clip,
      // und nicht in einer Liste, in der man die Stelle erst wiederfinden
      // muss. Der Kopf springt mit -- geschrieben wird über eine Stelle, die
      // man dabei sieht.
      {
        icon: 'captions',
        label: 'Hinweis dazu',
        gruppe: 'clip',
        onSelect: () => {
          u.select([clipId]);
          u.setPlayhead(Math.max(found.start, Math.min(at, found.start + found.duration)));
          u.setHinweisAnker(clipId);
          u.onOpenSheet('kommentare');
        },
      },
      // 7.74b: der Übergang zum vorigen Clip, von hier aus. Sascha, 8.
      // September: „Entweder am + Knopf. Oder im Kontextmenü oder zwischen den
      // Elementen“. Nur wo es einen vorigen Clip gibt -- sonst gäbe es nichts
      // zu blenden.
      ...(uebergangsZiel(u.timeline, found.start, [clipId])?.clipId === clipId
        ? [{
            icon: 'transition',
            label: found.transitionIn ? 'Übergang davor ändern' : 'Übergang davor',
            gruppe: 'clip',
            onSelect: () => {
              u.select([clipId]);
              u.onOpenSheet('uebergaenge');
            },
          } satisfies MenuItem]
        : []),
      // Und wieder weg (7.74g). Sascha, 11. September: „Wie mache ich einen
      // Übergang wieder weg?“ -- er musste fragen, und das ist die Antwort
      // auf die Frage. Im Blatt hiess das bisher „Harter Schnitt“: richtig
      // benannt, aber wer etwas wegnehmen will, sucht nach „weg“, nicht nach
      // dem Fachwort für das, was danach dasteht. Nur da, wo einer sitzt.
      ...(found.transitionIn
        ? [{
            icon: 'minus',
            label: 'Übergang entfernen',
            gruppe: 'clip',
            onSelect: () => {
              u.dispatch({ op: 'setTransition', clipId, transition: null } as Op);
            },
          } satisfies MenuItem]
        : []),
      // Keyframes auch hier: auf dem Telefon ist der lange Druck der kürzere
      // Weg als jede Leiste. `at` ist die Stelle, an der der Finger lag.
      {
        icon: keyframeAt(found, at - found.start, 1 / u.fps) !== null ? 'check' : 'plus',
        label: keyframeAt(found, at - found.start, 1 / u.fps) !== null ? 'Keyframe hier wegnehmen' : 'Keyframe hier setzen',
        gruppe: 'clip',
        onSelect: () => {
          const local = Math.min(found.duration, Math.max(0, at - found.start));
          const { op, gesetzt } = toggleKeyframe(found, local, u.fps);
          u.dispatch(op);
          u.setPlayhead(found.start + local);
          u.toast('success', gesetzt ? 'Keyframe gesetzt.' : 'Keyframe entfernt.');
        },
      },
      {
        icon: 'check',
        label: 'Weitere auswählen',
        gruppe: 'clip',
        onSelect: () => {
          if (!u.selection.includes(clipId)) u.select([...u.selection, clipId]);
          u.setMultiSelect(true);
        },
      },
    ];
    if (found.source.kind === 'text') items.push({ icon: 'text', label: 'Text ändern', gruppe: 'clip', onSelect: () => u.onOpenSheet('brand') });
    if (found.source.kind === 'asset') {
      items.push({
        icon: found.muted ? 'volume' : 'mute',
        label: found.muted ? 'Ton an' : 'Ton aus',
        gruppe: 'schnell',
        onSelect: () => u.dispatch({ op: 'updateClip', clipId, patch: { muted: !found.muted } } as Op),
      });
      // Schneiden nach Ton, auch von hier aus (7.65a). Sascha, 8. September:
      // „Audio ausklappen/anzeigen ins Kontextmenü packen. Weil das kommt ja
      // beim lang drauf bleiben.“ Der Weg über den Spurkopf gibt es weiter --
      // nur ist der Clip das, worauf man liegt, wenn einem der Gedanke kommt.
      if (spur.kind === 'video' && u.assetById.get(found.source.assetId)?.hasAudio) {
        items.push({
          icon: 'volume',
          label: u.tonkurven.includes(spur.id) ? 'Tonkurve einklappen' : 'Tonkurve ausklappen',
          gruppe: 'spur',
          onSelect: () => u.tonkurveUmschalten(spur.id),
        });
      }
    }
    // 7.14: Was am Clip sonst nur ueber Umwege ging.
    items.push({
      icon: 'text',
      label: 'Umbenennen',
      gruppe: 'clip',
      onSelect: () => u.setClipName({ clipId, name: found.label }),
    });
    if (found.source.kind === 'asset') {
      const asset = u.assetById.get(found.source.assetId);
      items.push({
        icon: 'plus',
        label: 'Medium ersetzen',
        gruppe: 'medium',
        onSelect: () => {
          u.setErsetzen(clipId);
          u.onOpenSheet('media');
        },
      });
      // Ein Bild anhalten und stehen lassen (S8). Sascha: „Ein Bild anhalten
      // und stehen lassen.“ Nur aus einem Video -- aus einem Bild wäre es eine
      // Kopie, und aus einem Ton gibt es nichts zu sehen.
      if (asset?.kind === 'video') {
        items.push({
          icon: 'pause',
          label: 'Standbild hier',
          gruppe: 'clip',
          onSelect: () => void standbildEinsetzen(u, found, spur.id, spur.clips, at, asset.id),
        });
        // An den Einstellungen trennen (7.66d). Der Fahrplan nennt die
        // Szenenerkennung als fehlende Zutat für den Rohschnitt -- sie ist
        // aber schon für sich die Arbeit, die man sonst hundertmal von Hand
        // macht: hinsehen, anhalten, trennen.
        items.push({
          icon: 'scissors',
          label: 'An den Einstellungen trennen',
          gruppe: 'mehr',
          onSelect: () => void anSzenenTrennen(u, found, asset),
        });
      }
      items.push({
        icon: 'film',
        label: 'Angaben',
        gruppe: 'mehr',
        onSelect: () => u.setAngaben(found.id),
      });
      // Saschas ausdruecklicher Wunsch: das Medium noch einmal durch die
      // Aufbereitung schicken, ohne den Umweg ueber die Medienbibliothek. Der
      // haeufige Fall ist ein Proxy, der schiefging -- man sieht es am Clip,
      // und dort soll auch der Knopf sein.
      items.push({
        icon: 'export',
        label: 'Erneut aufbereiten',
        gruppe: 'mehr',
        onSelect: () => {
          if (!asset) {
            u.toast('error', 'Das Medium dazu ist nicht mehr da.');
            return;
          }
          void api
            .reingest(asset.id)
            .then(() => u.toast('success', `„${asset.name}“ wird noch einmal aufbereitet.`))
            .catch((err: unknown) => u.toast('error', err instanceof Error ? err.message : 'Ging nicht.'));
        },
      });
      // Ein Standbild kommt selten im Format des Projekts. Fuellen statt
      // von Hand ziehen -- und zwar formatfuellend, nicht verzerrt.
      if (asset?.kind === 'image') {
        items.push({
          icon: 'check',
          label: 'Auf Bildschirmformat',
          gruppe: 'mehr',
          onSelect: () => {
            u.dispatch({
              op: 'updateClip',
              clipId,
              patch: { transform: { ...found.transform, x: 0.5, y: 0.5, scale: fuellFaktor(asset, u.settings), rotation: 0 } },
            } as Op);
            u.toast('success', 'Füllt jetzt das Bild.');
          },
        });
      }
    }
    items.push({
      icon: 'copy',
      label: 'Duplizieren',
      gruppe: 'schnell',
      onSelect: () => {
        const copy = duplicateClip(u.timeline, clipId);
        if (copy) {
          u.dispatch(copy.op);
          u.select([copy.id]);
        }
      },
    });
    items.push({
      icon: 'trash',
      label: 'Löschen',
      danger: true,
      gruppe: 'schnell',
      onSelect: () => {
        loescheClips([clipId]);
      },
    });
    return items;
}

/**
 * Das Standbild holen und einsetzen (S8).
 *
 * Zwei Schritte, und der erste kann schiefgehen: erst holt der Server das Bild
 * aus der Datei, dann wird es auf der Zeitleiste dazwischengeschoben. Die
 * Zeitleiste wird **erst danach** angefasst -- ginge das Holen daneben,
 * stünde sonst ein Clip da, der auf nichts zeigt.
 */
async function standbildEinsetzen(
  u: ClipMenuUmgebung,
  clip: ClipTyp,
  trackId: string,
  clips: readonly ClipTyp[],
  at: number,
  assetId: string,
): Promise<void> {
  const plan = standbildPlan(clips, clip.id, at, u.fps);
  if (!plan) return;
  u.toast('info', 'Standbild wird geholt …');
  let bild;
  try {
    bild = (await api.standbild(u.projectId, assetId, plan.quelle)).asset;
    // Ohne die neue Liste kennt die Vorschau das Medium nicht und malt ein
    // Loch, wo das Bild stehen soll.
    await u.refreshAssets();
  } catch (err: unknown) {
    u.toast('error', err instanceof Error ? err.message : 'Das Standbild ging nicht.');
    return;
  }

  const ops: Op[] = [];
  // Erst trennen, dann rücken: der zweite Teil entsteht an der alten Stelle
  // und wird gleich mit verschoben.
  if (plan.teilen) {
    const { links, rechts } = teileClip(clip, plan.start - clip.start);
    ops.push({ op: 'updateClip', clipId: clip.id, patch: links } as Op);
    ops.push({
      op: 'addClip',
      trackId,
      clip: { ...structuredClone(clip), id: newId('c'), ...rechts, start: rechts.start + plan.dauer, fadeIn: 0, transitionIn: null },
    } as Op);
  }
  for (const n of plan.nachruecker) {
    ops.push({ op: 'updateClip', clipId: n.clipId, patch: { start: n.start } } as Op);
  }
  const neuer = Clip.parse({
    id: newId('c'),
    source: { kind: 'asset', assetId: bild.id },
    start: plan.start,
    duration: plan.dauer,
    label: bild.name,
  });
  ops.push({ op: 'addClip', trackId, clip: neuer } as Op);
  u.dispatch(ops);
  u.select([neuer.id]);
  u.toast('success', `Standbild bei ${zeitText(plan.start)} — steht ${plan.dauer} Sekunden.`);
}

/**
 * Den Clip an seinen Einstellungswechseln zerlegen (7.66d).
 *
 * Die Liste entsteht beim Einlesen und liegt als Datei neben dem Medium --
 * hier wird sie nur gelesen. Drei Fälle werden auseinandergehalten, und das
 * ist der Punkt: es gibt keine Liste (das Medium ist älter als die
 * Erkennung), es gibt eine und sie ist in diesem Clip leer (eine einzige
 * Einstellung), oder es gibt Wechsel. „Keine gefunden“ zu sagen, wo nie
 * gesucht wurde, wäre eine Lüge.
 */
async function anSzenenTrennen(u: ClipMenuUmgebung, clip: ClipTyp, asset: AssetView): Promise<void> {
  if (!asset.szenenUrl) {
    u.toast('info', 'Für dieses Medium ist noch nicht erkannt, wo die Einstellung wechselt — „Erneut aufbereiten“ holt das nach.');
    return;
  }
  let datei: SzenenDatei;
  try {
    datei = (await (await fetch(asset.szenenUrl)).json()) as SzenenDatei;
  } catch {
    u.toast('error', 'Die Einstellungen liessen sich nicht lesen.');
    return;
  }
  const stuecke = anSzenen(clip, datei.schnitte ?? []);
  if (stuecke.length < 2) {
    u.toast('info', 'In diesem Clip wechselt die Einstellung nicht.');
    return;
  }
  // Dieselben Ops wie bei „Pausen raus“: aus einem Clip werden Stücke. Nur
  // fällt hier nichts weg -- die Stücke liegen lückenlos und ergeben zusammen
  // wieder den Clip.
  u.dispatch(pausenRaus(u.timeline, clip.id, stuecke));
  u.toast('success', `${stuecke.length} Einstellungen getrennt.`);
}
